<template>
  <footer class="bg-primary-accent z-1">
    <div class="container grid pt-10 pb-32 lg:pt-6 grid-cols-12 gap-x-6 lg:gap-x-16 gap-y-8">
      <div class="col-span-12 lg:hidden">
        <Search />
      </div>

      <!-- primary menu -->
      <nav
        v-if="footerContent.primary_menu_items"
        class="col-span-12 md:col-span-6 lg:col-span-4"
        :aria-label="translate('label.primary')"
      >
        <ul class="divide-y divide-gray-400">
          <li v-for="item in footerContent.primary_menu_items" class="" :key="item._uid">
            <NuxtLink
              class="block pt-5 pb-4 lg:pt-7 lg:pb-6 text-[28px] lg:text-[36px] font-semibold leading-[42px] hover:underline"
              :to="getStoryblokLink(item.link)"
              :target="item?.link?.target || `_self`"
            >
              {{ item.label }}
            </NuxtLink>
          </li>
        </ul>
      </nav>

      <!-- secondary menu -->
      <nav
        v-if="footerContent.secondary_menu_items"
        class="col-span-12 md:col-span-6 lg:col-span-4"
        :aria-label="translate('label.secondary')"
      >
        <ul class="divide-y divide-dashed divide-gray-300">
          <li v-for="item in footerContent.secondary_menu_items" :key="item._uid">
            <NuxtLink
              class="block pt-5 pb-4 lg:pt-7 lg:pb-6 text-[20px] lg:text-[24px] font-semibold leading-[42px] hover:underline"
              :to="getStoryblokLink(item.link)"
              :target="item?.link?.target || `_self`"
            >
              {{ item.label }}
            </NuxtLink>
          </li>
        </ul>
      </nav>

      <!-- contacts -->
      <div class="col-span-12 lg:col-span-4 pt-6">
        <div
          v-if="formattedContacts"
          class="prose text-xl prose-h2:text-3xl prose-h2:font-secondary prose-a:underline prose-a:transition-colors hover:prose-a:text-brand focus:prose-a:text-brand"
        >
          <h3 class="text-2xl my-0 font-medium">{{ footerContent.contactsLabel }}</h3>
          <Vue3RuntimeTemplate :template="formattedContacts"></Vue3RuntimeTemplate>
        </div>

        <div class="flex items-center space-x-8 mt-6">
          <template v-for="(certificate, idx) in footerContent.certificates" :key="idx">
            <NuxtLink
              v-if="certificate.content?.URL?.url"
              :href="certificate.content.URL.url"
              external
            >
              <img :src="certificate.content.logo.filename" loading="lazy" class="h-20 w-auto" />
            </NuxtLink>
            <span v-else>
              <img :src="certificate.content.logo.filename" loading="lazy" class="h-24 w-auto" />
            </span>
          </template>
        </div>
      </div>

      <div
        class="col-span-12 grid grid-cols-4 lg:grid-cols-12 pt-6 border-t border-gray-400 gap-12"
      >
        <div class="col-span-4 hidden lg:block">
          <Search />
        </div>

        <!-- newsletter -->
        <div class="col-span-4 order-last lg:order-last">
          <NewsletterComponent
            :description="newsletterData.description"
            :termsText="newsletterData.terms_label"
            terms-class-names="mt-3"
            terms-id="newsletter-footer-terms"
            input-class-names="flex flex-grow items-center px-4 py-2 bg-white border border-greyDark text-xl rounded-full"
            :placeholder="newsletterData.field_placeholder"
            :buttonLabel="newsletterData.button_label"
            :successMessage="newsletterData.success_message"
            @submit="handleNewsletterSubmit"
          >
            <template #description="{ description }">
              <h3 class="mb-2 text-gray-500">{{ description }}</h3>
            </template>
            <template #submit>
              <MxButton type="submit">
                <Icon class="text-2xl" name="ChevronRight" />
              </MxButton>
            </template>
          </NewsletterComponent>
        </div>

        <!-- social -->
        <nav
          class="col-span-4 flex flex-grow flex-wrap text-4xl lg:justify-end lg:items-start lg:pt-8 lg:order-last"
        >
          <NuxtLink
            v-for="network in socialNetworks"
            class="p-3 inline-flex"
            :href="network.value"
            :title="`${translate('action.follow_us')} ${network.name}`"
            :aria-label="`${translate('action.follow_us')} ${network.name}`"
            target="_blank"
            external
          >
            <Icon :name="network.name" />
          </NuxtLink>
        </nav>
      </div>

      <!-- partners -->
      <div class="col-span-12 pt-6 border-t border-gray-400">
        <h3 class="mb-4 text-gray-500">{{ footerContent.partnersLabel }}</h3>
        <nav
          class="flex flex-col space-y-8 lg:space-x-8 lg:flex-row lg:space-y-0 items-center justify-between"
        >
          <NuxtLink
            v-for="(partner, idx) in footerContent.partners"
            :key="idx"
            class="flex w-full h-[180px] lg:h-[80px] xl:h-[100px] py-12 lg:py-0 items-center justify-center bg-gray-200 lg:bg-transparent"
            :href="partner.content.URL.url"
            external
          >
            <img class="h-full max-w-[320px]" loading="lazy" :src="partner.content.logo.filename" />
          </NuxtLink>
        </nav>
      </div>

      <div class="col-span-12 pt-6 border-t border-gray-400">
        <h3 class="mb-2 text-gray-500">{{ footerContent.publicFundingLabel }}</h3>
        <p class="font-medium text-2xl">{{ publicFundingJoined }}</p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Vue3RuntimeTemplate from 'vue3-runtime-template';
import { getStoryblokLink } from '~/composables/storyblokLink';
import type { NewsletterContentType } from '~/types/common';
import { useTranslation } from '~/composables/useTranslation';
import { useNewsletter, type NewsletterFormData } from '~/composables/useNewsletter';
import type { ISbStoryData } from 'storyblok-js-client';
import Search from '~/components/Search.vue';

const translate = useTranslation();

const language = useState('language');
const footerContentMultiLang = useState<any>('footerContent');
const footerContent = computed(() => footerContentMultiLang.value[String(language.value)]);

const socialNetworks = useState<any[]>('socialNetworks');

const formattedContacts = computed(() => renderRichText(footerContent?.value?.contacts));
const publicFundingJoined = computed(() =>
  (footerContent.value.publicFunding || [])
    .map((sponsor: ISbStoryData<{ Name: string }>) =>
      sponsor.content.Name.length ? sponsor.content.Name : sponsor.name,
    )
    .join(' – '),
);

const newsletter = useNewsletter();
const newsletterContentMultilang = useState<{ [x: string]: NewsletterContentType }>(
  'newsletterContent',
);
const newsletterContent = computed(() => newsletterContentMultilang.value[String(language.value)]);
const newsletterData = computed(() => ({
  heading: newsletterContent.value.heading,
  description: newsletterContent.value.description,
  terms_label: renderRichText(newsletterContent.value.terms_label),
  button_label: newsletterContent.value.button_label,
  success_message: renderRichText(newsletterContent.value.success_message),
  field_placeholder: newsletterContent.value.field_placeholder,
}));
const handleNewsletterSubmit = async (data: NewsletterFormData) => newsletter.subscribe(data);
</script>
